import { useEffect, useState } from "react";
import { Link } from "react-router";

import {
  type EnvironmentSnapshot,
  type EnvironmentSummary,
  environmentSnapshots,
  listEnvironments,
} from "./profileClient";
import { useLatestRequest } from "./latest";

function message(caught: unknown): string {
  return caught instanceof Error ? caught.message : String(caught);
}

export function snapshotPath(snapshotId: string): string {
  return `/environment-profiles/snapshots/${encodeURIComponent(snapshotId)}`;
}

/** Customer Maximo environments and their immutable profile versions (DESIGN-SPEC 19). The
 * current version is the one that governs destination validation. */
export function EnvironmentsPage() {
  const [environments, setEnvironments] = useState<EnvironmentSummary[] | null>(null);
  const [selected, setSelected] = useState<EnvironmentSummary | null>(null);
  const [versions, setVersions] = useState<EnvironmentSnapshot[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const begin = useLatestRequest();

  useEffect(() => {
    listEnvironments()
      .then(setEnvironments)
      .catch((caught: unknown) => setError(message(caught)));
  }, []);

  async function show(environment: EnvironmentSummary) {
    const current = begin();
    setError(null);
    setSelected(environment);
    setVersions(null);
    try {
      const found = await environmentSnapshots(environment.id);
      if (current()) setVersions(found);
    } catch (caught) {
      if (current()) setError(message(caught));
    }
  }

  return (
    <section>
      <h1>Environment profiles</h1>
      <p>
        Each profile version is an immutable capture of a customer Maximo environment. The
        current version governs destination validation; generic product knowledge is shown
        beside it only as a reference.
      </p>
      {error && <p role="alert">{error}</p>}
      {environments === null && !error && <p>Loading environments…</p>}
      {environments !== null && environments.length === 0 && (
        <p>No environment has been profiled yet.</p>
      )}
      {environments !== null && environments.length > 0 && (
        <table className="grid">
          <caption>Environments</caption>
          <thead>
            <tr>
              <th>Environment</th>
              <th>Key</th>
              <th>Role</th>
              <th>Versions</th>
              <th>Current version</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {environments.map((environment) => (
              <tr key={environment.id}>
                <td>{environment.environment_name}</td>
                <td>{environment.profile_key}</td>
                <td>{environment.environment_role}</td>
                <td>{environment.snapshot_count}</td>
                <td>
                  {environment.current ? (
                    <Link to={snapshotPath(environment.current.snapshot_id)}>
                      {`${environment.profile_key}@v${environment.current.profile_version}`}
                    </Link>
                  ) : (
                    "not captured"
                  )}
                </td>
                <td>
                  <button type="button" onClick={() => void show(environment)}>
                    Versions of {environment.profile_key}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {selected && versions && (
        <table className="grid">
          <caption>Profile versions of {selected.environment_name}</caption>
          <thead>
            <tr>
              <th>Version</th>
              <th>Status</th>
              <th>Maximo</th>
              <th>Coverage</th>
              <th>Collected</th>
              <th>Method</th>
            </tr>
          </thead>
          <tbody>
            {versions.map((version) => (
              <tr key={version.id}>
                <td>
                  <Link to={snapshotPath(version.id)}>
                    {`${version.profile_key}@v${version.profile_version}`}
                  </Link>
                </td>
                <td>{version.status}</td>
                <td>{version.maximo_version}</td>
                <td>{version.capture_coverage}</td>
                <td>{version.collected_at}</td>
                <td>{version.collection_method}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
